import { createStyles } from 'antd-style';
import type { ReactNode } from 'react';

import { t } from '@/locales/i18n';
import { useRealmBrand } from '@/realm-profile/profile';

export interface GlobalLoadingProps {
  className?: string;
  minHeight?: number | string;
  subTitle?: ReactNode;
  testId?: string;
  title?: ReactNode;
}

/** useGlobalLoadingStyles 定义与旧工作台一致的品牌呼吸动效，避免加载过程退化为粗糙的默认 Spin。 */
const useGlobalLoadingStyles = createStyles(({ css, token }) => ({
  root: css`
    display: flex;
    min-height: 100dvh;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${token.paddingLG}px;
    text-align: center;
  `,
  image: css`
    width: 100px;
    animation: qiyun-global-loading-pulse 1.2s infinite;
    opacity: 0.4;

    @keyframes qiyun-global-loading-pulse {
      0%, 100% {
        opacity: 0.4;
      }

      50% {
        opacity: 1;
      }
    }
  `,
  title: css`
    margin-top: ${token.marginLG}px;
    color: ${token.colorTextHeading};
    font-size: ${token.fontSizeLG}px;
  `,
  subTitle: css`
    margin-top: ${token.marginXXS}px;
    color: ${token.colorTextSecondary};
    font-size: ${token.fontSize}px;
  `,
}));

/** 保留旧工作台的品牌过渡视觉；调用方只负责提供当前流程的文案。 */
export function GlobalLoading({
  className,
  minHeight,
  subTitle = t('Common:loadingResourcesDescription'),
  testId = 'global-loading',
  title = t('Common:loadingResources'),
}: GlobalLoadingProps) {
  const { cx, styles } = useGlobalLoadingStyles();
  const brand = useRealmBrand('', '/images/logo.png');

  return (
    <div
      className={cx(styles.root, className)}
      data-testid={testId}
      style={minHeight === undefined ? undefined : { minHeight }}
    >
      <img alt={brand.logoAlt} className={styles.image} src={brand.logoURL} />
      <div className={styles.title}>{title}</div>
      {subTitle ? <div className={styles.subTitle}>{subTitle}</div> : null}
    </div>
  );
}
